import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { 
  TreePine, 
  LayoutDashboard, 
  List, 
  Flag, 
  LogOut, 
  Menu, 
  X,
  BarChart2,
  Settings,
  ClipboardList,
  Bell
} from 'lucide-react';
import { auth } from '../../lib/firebase';
import toast from 'react-hot-toast';
import WeatherWidget from './WeatherWidget';
import ClockWidget from './ClockWidget';

export default function AdminNav() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const handleLogout = async () => {
    try {
      await auth.signOut();
      toast.success('Sesión cerrada correctamente');
      navigate('/login');
    } catch (error) {
      console.error('Error al cerrar sesión:', error);
      toast.error('Error al cerrar sesión');
    }
  };

  const navItems = [
    { 
      path: '/admin', 
      icon: LayoutDashboard, 
      label: 'Dashboard', 
      exact: true 
    },
    { 
      path: '/admin/trees', 
      icon: List, 
      label: 'Árboles'
    },
    { 
      path: '/admin/reports', 
      icon: Flag, 
      label: 'Reportes'
    },
    { 
      path: '/admin/statistics', 
      icon: BarChart2, 
      label: 'Estadísticas' 
    },
    { 
      path: '/admin/logs', 
      icon: ClipboardList, 
      label: 'Registros' 
    },
    { 
      path: '/admin/settings', 
      icon: Settings, 
      label: 'Configuración' 
    }
  ];

  const isActive = (path: string) => {
    if (path === '/admin' && path === location.pathname) return true;
    if (path !== '/admin' && location.pathname.startsWith(path)) return true;
    return false;
  };

  return (
    <div className="bg-white border-b border-gray-200 sticky top-0 z-40">
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-16">
          {/* Logo and Brand */}
          <div className="flex items-center">
            <Link to="/" className="flex items-center text-primary">
              <TreePine className="h-6 w-6 mr-2" />
              <span className="font-bold hidden md:inline">Panel de Administración</span>
            </Link>
          </div>

          {/* Weather and Clock */}
          <div className="hidden md:flex items-center space-x-4">
            <WeatherWidget />
            <ClockWidget />
          </div>

          {/* Right Actions */}
          <div className="flex items-center space-x-4">
            {/* Notifications */}
            <button className="hidden md:flex items-center text-gray-600 hover:text-gray-900 transition-colors">
              <Bell className="h-5 w-5" />
            </button>

            {/* Logout Button */}
            <button
              onClick={handleLogout}
              className="hidden md:flex items-center text-gray-600 hover:text-gray-900 transition-colors"
            >
              <LogOut className="h-5 w-5 mr-2" />
              <span>Cerrar Sesión</span>
            </button>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="md:hidden p-2 text-gray-600 hover:text-primary transition-colors"
            >
              {isMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>

        {/* Navigation - Desktop */}
        <div className="hidden md:block border-t border-gray-100">
          <nav className="flex space-x-1 py-3">
            {navItems.map(({ path, icon: Icon, label }) => (
              <Link
                key={path}
                to={path}
                className={`flex items-center px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  isActive(path)
                    ? 'bg-primary text-white'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                <Icon className="h-4 w-4 mr-2" />
                {label}
              </Link>
            ))}
          </nav>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="md:hidden border-t border-gray-100">
            {/* Weather and Clock - Mobile */}
            <div className="p-4 space-y-2">
              <WeatherWidget />
              <ClockWidget />
            </div>

            {/* Mobile Navigation */}
            <nav className="py-3 space-y-1">
              {navItems.map(({ path, icon: Icon, label }) => (
                <Link
                  key={path}
                  to={path}
                  onClick={() => setIsMenuOpen(false)}
                  className={`flex items-center px-4 py-3 text-sm font-medium ${
                    isActive(path)
                      ? 'bg-primary text-white'
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  <Icon className="h-4 w-4 mr-3" />
                  {label}
                </Link>
              ))}
            </nav>

            {/* Mobile Logout */}
            <div className="border-t border-gray-100 p-4">
              <button
                onClick={handleLogout}
                className="w-full flex items-center justify-center px-4 py-2 text-sm font-medium text-red-600 hover:bg-red-50 rounded-lg transition-colors"
              >
                <LogOut className="h-4 w-4 mr-2" />
                Cerrar Sesión
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}